

import { GoogleMap, Marker, MarkerClusterer, InfoWindow } from '@react-google-maps/api';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BaseMapProps, MapCoordinates, MapMarkerData } from './types';
import { GOOGLE_MAPS_API_KEY } from '../../../../lib/constant';
import { useGoogleMaps } from './google-maps-global-provider';
import { MapInfoWindowContent } from './map-info-window-content';


const defaultMapOptions = {
  disableDefaultUI: false,
  zoomControl: false,
  mapTypeControl: true,
  fullscreenControl: true,
  streetViewControl: false,
  styles: [
    // You can add custom map styles here
  ]
};

/**
 * Create a data URI for a cluster icon SVG
 * Works in both Vite.js and Next.js - Data URIs are standard JavaScript
 * 
 * @param size - Size of the icon (width and height)
 * @param color - Fill color for the circle
 * @param strokeColor - Stroke color (default: white)
 * @returns Data URI string for the SVG icon
 */
function createClusterIconDataUri(
  size: number,
  color: string,
  strokeColor: string = 'white'
): string {
  const center = size / 2;
  const radius = size / 2 - 2;

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${center}" cy="${center}" r="${radius}" fill="${color}" stroke="${strokeColor}" stroke-width="2"/>
    </svg>
  `.trim();

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

/**
 * Default cluster styles for Google Maps MarkerClusterer
 * These styles work in both Vite.js and Next.js
 */
const defaultClusterStyles = [
  {
    textColor: 'white',
    url: createClusterIconDataUri(40, '#51cf66'),
    height: 40,
    width: 40,
    textSize: 12
  },
  {
    textColor: 'white',
    url: createClusterIconDataUri(45, '#ffd43b'),
    height: 45,
    width: 45,
    textSize: 13
  },
  {
    textColor: 'white',
    url: createClusterIconDataUri(50, '#ff8c00'),
    height: 50,
    width: 50,
    textSize: 14
  },
  {
    textColor: 'white',
    url: createClusterIconDataUri(55, '#ff6b6b'),
    height: 55,
    width: 55,
    textSize: 15
  },
  {
    textColor: 'white',
    url: createClusterIconDataUri(60, '#8b0000'),
    height: 60,
    width: 60,
    textSize: 16
  }
];

/**
 * @deprecated Utilisez `GoogleMapEngine` depuis `@repo/ui/components/google-map-engine`.
 *
 * Cette implémentation historique est partielle (pas de support polygones, pas de
 * Street View modal, clé API lue d'un singleton en dur). Elle est conservée pour
 * compatibilité mais ne devrait plus être utilisée dans de nouveaux écrans.
 */
export const GoogleMapsProvider: React.FC<BaseMapProps> = ({
  options,
  markers = [],
  clustering = { enabled: true },
  eventHandlers,
  loading = false,
  className = "",
  style = { width: '100%', height: '100%' },
  renderInfoWindow
}) => {

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<MapMarkerData | null>(null);
  const isProgrammaticMove = useRef(false);

  // Use global Google Maps context instead of loading directly
  const { isLoaded, loadError } = useGoogleMaps();

  // API key is handled by the global provider, but we keep it for error messages
  const apiKey = GOOGLE_MAPS_API_KEY;

  const clustererKey = useMemo(() => {
    const radius = clustering.maxClusterRadius ?? 60;
    return `${clustering.enabled ? 'on' : 'off'}-${markers.length}-${radius}`;
  }, [clustering.enabled, clustering.maxClusterRadius, markers.length]);

  const mapOptions = {
    ...defaultMapOptions,
    zoom: options.zoom,
    center: { lat: options.center.lat, lng: options.center.lng },
    scrollWheelZoom: options.scrollWheelZoom,
    dragging: options.dragging,
    zoomControl: options.zoomControl,
    mapTypeControl: false,
    fullscreenControl: options.fullscreenControl,
    streetViewControl: false,
    styles: options.styles || defaultMapOptions.styles,
  };


  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {

    if (event.latLng && eventHandlers?.onMapClick) {
      const coordinates: MapCoordinates = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };
      eventHandlers.onMapClick(coordinates);
    }
  }, [eventHandlers]);

  const handleMapMove = useCallback(() => {
    if (map && eventHandlers?.onMapMove && !isProgrammaticMove.current) {
      const center = map.getCenter();
      const zoom = map.getZoom();
      if (center && zoom !== undefined) {
        eventHandlers.onMapMove(
          { lat: center.lat(), lng: center.lng() },
          zoom
        );
      }
    }
  }, [map, eventHandlers]);

  const handleBoundsChanged = useCallback(() => {
    if (map && eventHandlers?.onBoundsChanged) {
      const bounds = map.getBounds();
      if (bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        eventHandlers.onBoundsChanged({
          north: ne.lat(),
          south: sw.lat(),
          east: ne.lng(),
          west: sw.lng()
        });
      }
    }
  }, [map, eventHandlers]);

  const handleMarkerClick = useCallback((marker: MapMarkerData) => {
    // Set selected marker to show InfoWindow
    setSelectedMarker(marker);

    // Center the map on the clicked marker
    if (map) {
      isProgrammaticMove.current = true;
      map.setCenter({ lat: marker.position.lat, lng: marker.position.lng });
      // Optionally zoom in a bit when centering on a marker
      const currentZoom = map.getZoom();
      if (currentZoom && currentZoom < 15) {
        map.setZoom(15);
      }
      // Reset the flag after a short delay
      setTimeout(() => {
        isProgrammaticMove.current = false;
      }, 100);
    }

    // Don't call the marker click handler - we show InfoWindow instead
    // if (eventHandlers?.onMarkerClick) {
    //   eventHandlers.onMarkerClick(marker);
    // }
  }, [map]);

  const handleViewDetails = useCallback((inventaireId: string) => {
    // Close the InfoWindow
    setSelectedMarker(null);
    // Call the onViewDetails handler if provided
    if (eventHandlers?.onViewDetails) {
      eventHandlers.onViewDetails(inventaireId);
    }
  }, [eventHandlers]);


  // Update map center when options change (but not when centering from marker click)
  useEffect(() => {
    if (map && options.center && !isProgrammaticMove.current) {
      isProgrammaticMove.current = true;
      map.setCenter({ lat: options.center.lat, lng: options.center.lng });
      setTimeout(() => {
        isProgrammaticMove.current = false;
      }, 100);
    }
  }, [map, options.center]);

  // Update map zoom when options change
  useEffect(() => {
    if (map && options.zoom !== undefined && !isProgrammaticMove.current) {
      isProgrammaticMove.current = true;
      map.setZoom(options.zoom);
      setTimeout(() => {
        isProgrammaticMove.current = false;
      }, 100);
    }
  }, [map, options.zoom]);

  if (!apiKey || loadError) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`} style={style}>
        <div className="text-center p-4">
          <p className="text-red-600 font-medium">
            {!apiKey ? 'Google Maps API Key Required' : 'Erreur de chargement de la carte'}
          </p>
          <p className="text-sm text-gray-600">
            {!apiKey
              ? 'Please set VITE_GOOGLE_MAPS_API_KEY in your environment variables'
              : loadError?.message || 'Failed to load Google Maps'
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={style}>
      {(loading || !isLoaded) && (
        <div className="absolute inset-0 bg-black/20 z-[999] flex items-center justify-center">
          <div className="bg-white p-4 rounded-lg shadow-lg flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span>Chargement de la carte...</span>
          </div>
        </div>
      )}

      {isLoaded && (
        <GoogleMap
          mapContainerStyle={style}
          center={{ lat: options.center.lat, lng: options.center.lng }}
          zoom={options.zoom}
          options={mapOptions}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={handleMapClick}
          onDragEnd={handleMapMove}
          onZoomChanged={handleMapMove}
          onBoundsChanged={handleBoundsChanged}

        >
          {clustering.enabled ? (
            <MarkerClusterer
              key={clustererKey}
              options={{
                maxZoom: 20,
                gridSize: clustering.maxClusterRadius || 60,
                averageCenter: true,
                ignoreHidden: true,
                styles: defaultClusterStyles
              }}
            >
              {(clusterer) => (
                <>
                  {markers.map((marker) => (
                    <Marker
                      key={marker.id}
                      position={{ lat: marker.position.lat, lng: marker.position.lng }}
                      title={marker.title}
                      clusterer={clusterer}
                      onClick={() => handleMarkerClick(marker)}
                      icon={marker?.data?.icon ? {
                        url: marker.data.icon,
                        scaledSize: new google.maps.Size(30, 30)
                      } : undefined}
                    />
                  ))}

                  {/* InfoWindow for selected marker */}
                  {selectedMarker && (
                    <InfoWindow
                      position={{ lat: selectedMarker.position.lat, lng: selectedMarker.position.lng }}
                      onCloseClick={() => setSelectedMarker(null)}
                      options={{
                        pixelOffset: new google.maps.Size(0, -10),
                        maxWidth: 280,
                        minWidth: 250
                      }}
                    >
                      <>
                        {renderInfoWindow ? (
                          renderInfoWindow(selectedMarker)
                        ) : (
                          <MapInfoWindowContent
                            marker={selectedMarker}
                            onViewDetails={handleViewDetails}
                          />
                        )}
                      </>
                    </InfoWindow>
                  )}
                </>
              )}
            </MarkerClusterer>
          ) : (
            <>
              {markers.map((marker) => (
                <Marker
                  key={marker.id}
                  position={{ lat: marker.position.lat, lng: marker.position.lng }}
                  title={marker.title}
                  onClick={() => handleMarkerClick(marker)}
                  icon={marker?.data?.icon ? {
                    url: marker.data.icon,
                    scaledSize: new google.maps.Size(30, 30)
                  } : undefined}
                />
              ))}

              {/* InfoWindow for selected marker */}
              {selectedMarker && (
                <InfoWindow
                  position={{ lat: selectedMarker.position.lat, lng: selectedMarker.position.lng }}
                  onCloseClick={() => setSelectedMarker(null)}
                  options={{
                    pixelOffset: new google.maps.Size(0, -10),
                    maxWidth: 280,
                    minWidth: 250
                  }}
                >
                  <>
                    {renderInfoWindow ? (
                      renderInfoWindow(selectedMarker)
                    ) : (
                      <MapInfoWindowContent
                        marker={selectedMarker}
                        onViewDetails={handleViewDetails}
                      />
                    )}
                  </>
                </InfoWindow>
              )}
            </>
          )}
        </GoogleMap>
      )}
    </div>
  );
};

GoogleMapsProvider.displayName = 'GoogleMapsProvider';